package com.taocent.simple.compose.component.richtext

import androidx.compose.animation.Crossfade
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.composables.icons.feather.Feather
import com.composables.icons.feather.Hash
import com.composables.icons.feather.Link
import com.composables.icons.feather.Tool
import com.composables.icons.lucide.Keyboard
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Redo2
import com.composables.icons.lucide.Smile
import com.composables.icons.lucide.Type
import com.composables.icons.lucide.Undo2
import com.taocent.simple.compose.component.richtext.core.LocalRichTextConfig
import com.taocent.simple.compose.component.richtext.core.RichTextConfig
import com.taocent.simple.compose.component.richtext.core.RichTextState
import com.taocent.simple.compose.component.richtext.core.internal.dialog.ExportJsonDialog
import com.taocent.simple.compose.component.richtext.core.internal.dialog.LinkDialog
import com.taocent.simple.compose.component.richtext.core.internal.platform.rememberKeyboardHeight
import com.taocent.simple.compose.component.richtext.core.internal.toolbar.ToolbarCategoryItem
import com.taocent.simple.compose.component.richtext.core.internal.ui.EmptyTextContextMenuProvider
import com.taocent.simple.compose.component.richtext.core.internal.ui.EmptyTextToolbar
import com.taocent.simple.compose.component.richtext.core.internal.ui.EditorPanelController
import com.taocent.simple.compose.component.richtext.core.internal.ui.EditorScaffold
import com.taocent.simple.compose.component.richtext.core.internal.ui.panel.EmojiPanel
import com.taocent.simple.compose.component.richtext.core.internal.ui.panel.TextStylePanel
import com.taocent.simple.compose.component.richtext.core.internal.ui.rememberEditorPanelController
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuDropdownProvider
import androidx.compose.foundation.text.contextmenu.provider.LocalTextContextMenuToolbarProvider
import androidx.compose.ui.platform.LocalTextToolbar
import kotlinx.coroutines.delay

/**
 * 开箱即用的富文本编辑器 — 内部组合 [RichTextTextField] / [RichTextToolbar] / [RichTextPanel] / [RichTextEditorDialogs]。
 *
 * 工具栏 / 面板的高度与状态机委托给 :richtext-core 的 [EditorPanelController] / [EditorScaffold]
 * (与 BlockRichTextEditor 共用),避免两份实现各自维护一份 panel height 状态机。
 */
@Composable
fun RichTextEditor(
    state: RichTextState,
    modifier: Modifier = Modifier,
    placeholder: String = "请输入文本...",
    config: RichTextConfig? = null,
    focusRequester: FocusRequester = remember { FocusRequester() },
    editorState: RichTextEditorState = rememberRichTextEditorState(),
) {
    // 顶层固定提供的 config:调用方未传时使用 core 内置的 RichTextConfig()(已包含
    // DefaultCustomEmojiIcons),无需再 copy() 注入。
    val effectiveConfig = config ?: LocalRichTextConfig.current
    CompositionLocalProvider(
        LocalRichTextConfig provides effectiveConfig,
        // 屏蔽系统文本工具栏:
        // - 1.11+ 新机制([LocalTextContextMenuToolbarProvider] / [LocalTextContextMenuDropdownProvider]):
        //   选词/长按/双击/右键会调 `provider.showTextContextMenu(...)`,默认 Android 走系统
        //   ActionMode / iOS 走 EditMenu / Desktop 走 Compose Popup —— 全部用 no-op 屏蔽。
        // - 旧机制([LocalTextToolbar]):KMP 1.11.0 Desktop / iOS / js / wasmJs 仍走这里,作为 fallback。
        LocalTextContextMenuDropdownProvider provides EmptyTextContextMenuProvider,
        LocalTextContextMenuToolbarProvider provides EmptyTextContextMenuProvider,
        LocalTextToolbar provides EmptyTextToolbar,
    ) {
        Column(modifier = modifier) {
            RichTextTextField(
                state = state,
                modifier = Modifier.weight(1f),
                placeholder = placeholder,
                focusRequester = focusRequester,
            )
            RichTextEditorContainer(
                state = state,
                editorState = editorState,
                focusRequester = focusRequester,
            )
        }
    }
    RichTextEditorDialogs(
        state = state,
        editorState = editorState,
        focusRequester = focusRequester,
    )
}

/**
 * 工具栏 + 展开面板的组合容器(横线分隔)。
 *
 * 高度与状态机由 [EditorPanelController] 提供,与 :blockrichtext 共用一份实现。
 */
@Composable
internal fun RichTextEditorContainer(
    state: RichTextState,
    editorState: RichTextEditorState,
    focusRequester: FocusRequester,
    modifier: Modifier = Modifier,
) {
    val config = LocalRichTextConfig.current
    val panelController = rememberEditorPanelController(config.panel.defaultHeight)

    // 键盘出现时,清空 activeCategory(与 :blockrichtext 行为一致)。
    LaunchedEffect(panelController.isKeyboardVisible) {
        if (panelController.isKeyboardVisible) {
            editorState.clearCategory()
        }
    }

    EditorScaffold(
        onUserInteraction = { state.saveSelection() },
        panelController = panelController,
        modifier = modifier,
        toolbar = {
            RichTextToolbar(
                state = state,
                editorState = editorState,
                focusRequester = focusRequester,
                isKeyboardVisible = panelController.isKeyboardVisible,
                onPanelOpenRequested = { panelController.requestExpand() },
                onKeyboardCollapseRequested = { panelController.requestKeyboardCollapse() },
                onKeyboardExpandRequested = {
                    panelController.requestKeyboardExpand(
                        hasActiveCategory = editorState.activeCategory != null
                    )
                },
            )
        },
        panel = { controller ->
            val animatedHeight = controller.animatedPanelHeight(
                hasActiveCategory = editorState.activeCategory != null
            )
            CompositionLocalProvider(LocalPanelHeightOrNull provides animatedHeight) {
                RichTextPanel(
                    state = state,
                    editorState = editorState,
                    focusRequester = focusRequester,
                )
            }
        }
    )
}

/**
 * 富文本工具栏 — 撤销/重做/表情/文字样式/链接/@提及/导出/键盘切换。
 */
@Composable
fun RichTextToolbar(
    state: RichTextState,
    editorState: RichTextEditorState,
    focusRequester: FocusRequester,
    modifier: Modifier = Modifier,
    isKeyboardVisible: Boolean = (LocalSoftwareKeyboardController.current != null && rememberKeyboardHeight() > 0.dp),
    keyboardController: androidx.compose.ui.platform.SoftwareKeyboardController? = LocalSoftwareKeyboardController.current,
    onPanelOpenRequested: () -> Unit = {},
    onKeyboardCollapseRequested: () -> Unit = {},
    onKeyboardExpandRequested: () -> Unit = {},
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .focusProperties { canFocus = false }
            .background(MaterialTheme.colorScheme.surface),
    ) {
        Row(
            modifier = Modifier
                .weight(1f)
                .horizontalScroll(rememberScrollState()),
        ) {
            ToolbarCategoryItem(
                icon = { Image(Lucide.Undo2, contentDescription = "撤销") },
                isActive = false,
                enabled = state.canUndo,
                onClick = { state.undo() },
            )
            ToolbarCategoryItem(
                icon = { Image(Lucide.Redo2, contentDescription = "重做") },
                isActive = false,
                enabled = state.canRedo,
                onClick = { state.redo() },
            )
            ToolbarCategoryItem(
                icon = { Image(Lucide.Smile, contentDescription = null) },
                isActive = editorState.activeCategory == ToolCategory.EMOJI,
                onClick = {
                    val willOpen = editorState.activeCategory != ToolCategory.EMOJI
                    if (willOpen) {
                        onPanelOpenRequested()
                        if (isKeyboardVisible) keyboardController?.hide()
                    }
                    editorState.toggleCategory(ToolCategory.EMOJI)
                },
            )
            ToolbarCategoryItem(
                icon = { Image(Lucide.Type, contentDescription = null) },
                isActive = editorState.activeCategory == ToolCategory.TEXT_STYLE,
                onClick = {
                    val willOpen = editorState.activeCategory != ToolCategory.TEXT_STYLE
                    if (willOpen) {
                        onPanelOpenRequested()
                        if (isKeyboardVisible) keyboardController?.hide()
                    }
                    editorState.toggleCategory(ToolCategory.TEXT_STYLE)
                },
            )
            ToolbarCategoryItem(
                icon = { Image(Feather.Link, contentDescription = null) },
                isActive = false,
                onClick = { editorState.requestLinkDialog(isKeyboardVisible) },
            )
            ToolbarCategoryItem(
                icon = { Image(Feather.Hash, contentDescription = null) },
                isActive = false,
                onClick = {
                    state.restoreSavedSelection()
                    state.insertHyperlink("@赤红", "")
                },
            )
            ToolbarCategoryItem(
                icon = { Image(Feather.Tool, contentDescription = null) },
                isActive = false,
                onClick = { editorState.requestExportDialog(isKeyboardVisible) },
            )
        }
        ToolbarCategoryItem(
            icon = { Image(Lucide.Keyboard, contentDescription = "键盘") },
            isActive = false,
            onClick = {
                if (isKeyboardVisible) {
                    onKeyboardCollapseRequested()
                    keyboardController?.hide()
                } else {
                    onKeyboardExpandRequested()
                    state.tryRequestFocusIfHadFocus(focusRequester)
                    keyboardController?.show()
                }
            },
        )
    }
}

/**
 * 内部 CompositionLocal — 由 [RichTextEditorContainer] 提供其计算出来的 animatedPanelHeight,
 * 供同一组合树下的 [RichTextPanel] 默认参数消费。独立使用 [RichTextPanel] 时为 null,
 * 此时 Panel 内部自行计算。
 */
private val LocalPanelHeightOrNull = compositionLocalOf<Dp?> { null }

/**
 * 富文本展开面板 — 根据 [RichTextEditorState.activeCategory] 渲染 EmojiPanel / TextStylePanel。
 */
@Composable
fun RichTextPanel(
    state: RichTextState,
    editorState: RichTextEditorState,
    focusRequester: FocusRequester,
    modifier: Modifier = Modifier,
    animatedPanelHeight: Dp = LocalPanelHeightOrNull.current ?: rememberAnimatedPanelHeight(editorState),
) {
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .height(animatedPanelHeight),
        color = MaterialTheme.colorScheme.surfaceContainerLow,
        tonalElevation = 2.dp,
    ) {
        Crossfade(
            targetState = editorState.activeCategory,
            animationSpec = androidx.compose.animation.core.tween(durationMillis = 200),
        ) { category ->
            when (category) {
                ToolCategory.EMOJI -> {
                    val emojiSizeSp = state.currentSpanStyle().fontSize.let {
                        if (it != TextUnit.Unspecified) it else 16.sp
                    }
                    EmojiPanel(
                        onEmojiSelected = { emoji ->
                            state.restoreSavedSelection()
                            state.insertText(emoji)
                            state.tryRequestFocusIfHadFocus(focusRequester)
                        },
                        onCustomEmojiSelected = { emojiId ->
                            state.restoreSavedSelection()
                            state.insertCustomEmoji(emojiId, emojiSizeSp)
                            state.tryRequestFocusIfHadFocus(focusRequester)
                        },
                    )
                }
                ToolCategory.TEXT_STYLE -> TextStylePanel(
                    state = state,
                    restoreSelection = { state.restoreSavedSelection() },
                    onActionCompleted = { state.tryRequestFocusIfHadFocus(focusRequester) },
                )
                else -> {}
            }
        }
    }
}

/**
 * 独立使用 [RichTextPanel] 时(无 [RichTextEditorContainer] 注入 height)由本函数
 * 自动计算高度。
 */
@Composable
private fun rememberAnimatedPanelHeight(editorState: RichTextEditorState): Dp {
    val keyboardHeightDp = rememberKeyboardHeight()
    val isKeyboardVisible = keyboardHeightDp > 0.dp
    val config = LocalRichTextConfig.current
    val defaultPanelHeight = config.panel.defaultHeight
    if (editorState.rememberedKeyboardHeight < defaultPanelHeight) {
        editorState.rememberedKeyboardHeight = defaultPanelHeight
    }
    if (isKeyboardVisible && keyboardHeightDp > editorState.rememberedKeyboardHeight) {
        editorState.rememberedKeyboardHeight = keyboardHeightDp
    }
    val target = when {
        editorState.activeCategory != null -> editorState.rememberedKeyboardHeight
        isKeyboardVisible -> keyboardHeightDp
        else -> 0.dp
    }
    val animated by animateDpAsState(
        targetValue = target,
        animationSpec = spring(
            dampingRatio = androidx.compose.animation.core.Spring.DampingRatioNoBouncy,
            stiffness = androidx.compose.animation.core.Spring.StiffnessMediumLow,
        ),
    )
    return animated
}

/**
 * 对话框层 — 链接对话框 + 导出 JSON 对话框。
 */
@Composable
fun RichTextEditorDialogs(
    state: RichTextState,
    editorState: RichTextEditorState,
    focusRequester: FocusRequester,
) {
    val keyboardController = LocalSoftwareKeyboardController.current

    if (editorState.showLinkDialog) {
        LinkDialog(
            state = state,
            restoreSelection = { state.restoreSavedSelection() },
            onDismiss = { editorState.dismissLinkDialog() },
            onConfirm = { editorState.dismissLinkDialog() },
        )
    }
    var linkDialogEverShown by remember { mutableStateOf(false) }
    if (editorState.showLinkDialog) {
        linkDialogEverShown = true
    }
    LaunchedEffect(editorState.showLinkDialog) {
        if (!editorState.showLinkDialog && linkDialogEverShown) {
            val shouldRestoreKeyboard = editorState.consumeKeyboardRestoreRequest()
            if (shouldRestoreKeyboard) {
                delay(200)
                state.tryRequestFocusIfHadFocus(focusRequester)
                keyboardController?.show()
            }
        }
    }

    if (editorState.showExportDialog) {
        val jsonContent = remember { state.toJson() }
        ExportJsonDialog(
            jsonContent = jsonContent,
            onDismiss = { editorState.dismissExportDialog() },
        )
    }
    var exportDialogEverShown by remember { mutableStateOf(false) }
    if (editorState.showExportDialog) {
        exportDialogEverShown = true
    }
    LaunchedEffect(editorState.showExportDialog) {
        if (!editorState.showExportDialog && exportDialogEverShown) {
            val shouldRestoreKeyboard = editorState.consumeKeyboardRestoreRequest()
            if (shouldRestoreKeyboard) {
                delay(200)
                state.tryRequestFocusIfHadFocus(focusRequester)
                keyboardController?.show()
            }
        }
    }
}
